import type { MeResponse, ProductDto, SupportCountsDto } from '@exam-prep/contracts';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { get } from './api';
import { hasPermission, useAuth } from './auth';
import type { Infer } from './infer';

export type SupportCounts = Infer<typeof SupportCountsDto>;

const KEY = 'admin.product';

/** Products the admin holds `permission` on, keeping the API's order. */
export function productsWith<P extends { id: string }>(
  me: MeResponse | null | undefined,
  products: readonly P[] | undefined,
  permission: string,
): P[] {
  return (products ?? []).filter((p) => hasPermission(me, permission, p.id));
}

/** The product a page should work on: the remembered one if still allowed, else the first. */
export function pickProduct<P extends { id: string }>(
  allowed: readonly P[],
  remembered: string | null | undefined,
): P | undefined {
  return allowed.find((p) => p.id === remembered) ?? allowed[0];
}

/** Open tickets across every queue (for the sidebar badge). */
export const totalOpen = (counts: SupportCounts | undefined): number =>
  Object.values(counts?.open ?? {}).reduce((n, v) => n + v, 0);

const read = (): string | null => {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
};

/**
 * The product the SSC Rankers League, Early-bird and Support pages work on. Today there is one product,
 * so this is invisible; with several, the page shows a small switcher and remembers the choice.
 */
export function useActiveProduct(permission: string) {
  const { me } = useAuth();
  const products = useQuery({
    queryKey: ['products'],
    queryFn: () => get<ProductDto[]>('/admin/products'),
  });
  const [remembered, setRemembered] = useState(read);
  const allowed = productsWith(me, products.data, permission);
  const current = pickProduct(allowed, remembered);
  const select = useCallback((id: string) => {
    setRemembered(id);
    try {
      localStorage.setItem(KEY, id);
    } catch {
      /* private mode */
    }
  }, []);
  return {
    product: current,
    products: allowed,
    select,
    loading: products.isPending,
    error: products.error,
  };
}
